import Navbar from "layouts/ecommerce/posnavbar/Navbar";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useStopwatch } from "react-timer-hook";
import useGetDevice from "../hooks/useGetDevice";
import usePrint from "../hooks/usePrint";
import "./CompleteOrder.css";

export const CompleteCreditOrder = () => {
  const isMobile = useGetDevice();
  const navigate = useNavigate();
  const location = useLocation();
  const { amount, paymentMethod, settlementId, customerName, customerMobile } =
    location?.state || {};
  const { seconds, minutes } = useStopwatch({ autoStart: true });
  const { startPrint, isPrinting } = usePrint();

  const showTimer = !isMobile;

  useEffect(() => {
    if (showTimer && (seconds >= 30 || minutes > 0)) {
      const audio = new Audio(
        "https://media.geeksforgeeks.org/wp-content/uploads/20190531135120/beep.mp3"
      );
      audio.play();
    }
  }, [seconds, minutes, showTimer]);

  const handleRedirect = () => {
    navigate("/orders", {
      state: { initialTab: "Order History", openCreditSummary: true },
    });
  };

  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === "Enter") {
        handleRedirect();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, []);

  const getPaymentMethodDisplay = () => {
    const method = paymentMethod || "CASH";
    const lower = method.toLowerCase().replace(/_/g, " ");
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  };

  const handlePrintBill = () => {
    startPrint({
      printType: "credit-settlement",
      settlementData: { amount, paymentMethod, settlementId, customerName, customerMobile },
    });
  };

  useEffect(() => {
    const handleF12 = (event) => {
      if (event.key === "F12") {
        event.preventDefault();
        if (!isPrinting) {
          handlePrintBill();
        }
      }
    };
    window.addEventListener("keydown", handleF12);
    return () => window.removeEventListener("keydown", handleF12);
  }, [isPrinting, amount, paymentMethod, settlementId, customerName, customerMobile]);

  return (
    <Navbar>
      <div className="main-box">
        <div className="container-box">
          <div className="success-icon-wrapper">
            <div style={{ transform: "none" }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="success-icon-svg"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <path d="m9 12 2 2 4-4"></path>
              </svg>
            </div>
            <div className="success-icon-border-pulse"></div>
          </div>

          <h2 className="order-success-title" data-testid="text-payment-success-title">
            Payment Successful!
          </h2>

          <p className="order-id-text" data-testid="text-payment-success-amount">
            Amount of ₹{amount ?? "..."} received
          </p>

          <p className="payment-method-text" data-testid="text-payment-success-method">
            Settled via {getPaymentMethodDisplay()}
          </p>

          {settlementId && <p className="order-id-note">Settlement ID: {settlementId}</p>}

          <div className="payment-details-container">
            {!isMobile && (
              <div className="timer-wrapper">
                <p className="timer-instruction-text">
                  30 seconds will be the maximum time for closing the drawer.
                </p>
              </div>
            )}
            {paymentMethod === "CASH" && <h3 className="change-refund-text">Change: ₹0.00</h3>}
          </div>

          <div className="action-buttons-grid">
            <button
              className="action-button-item"
              data-testid="button-print-bill"
              disabled={isPrinting}
              onClick={handlePrintBill}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="action-button-icon"
                aria-hidden="true"
              >
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                <path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6"></path>
                <rect x="6" y="14" width="12" height="8" rx="1"></rect>
              </svg>
              <span>{isPrinting ? "Printing..." : "Print Bill (F12)"}</span>
            </button>

            <button className="action-button-item" data-testid="button-email-invoice" disabled>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="action-button-icon"
                aria-hidden="true"
              >
                <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"></path>
                <rect x="2" y="4" width="20" height="16" rx="2"></rect>
              </svg>
              <span>E-Invoice</span>
            </button>
          </div>

          <div className="order-complete-button-container">
            <button
              className="continue-button"
              data-testid="button-go-home"
              onClick={handleRedirect}
            >
              Go to Home{" "}
              {showTimer && (
                <span
                  className={seconds >= 30 || minutes > 0 ? "timer-text-red" : "timer-text-white"}
                >
                  ({minutes}:{seconds.toString().padStart(2, "0")})
                </span>
              )}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="continue-button-icon"
                aria-hidden="true"
              >
                <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"></path>
                <path d="M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </Navbar>
  );
};
